/**
 * NOVA CHARTER — adaptive generative score.
 *
 * There is no music file. The score is a five-layer synth ensemble in D minor at 78 BPM,
 * scheduled by a lookahead clock (a 25 ms `setInterval` that writes notes 200 ms into
 * the future) so timing comes from the audio hardware, not from timer jitter.
 *
 * Layers, and when they are present:
 *
 * | layer     | role                                | brought in by                     |
 * |-----------|-------------------------------------|-----------------------------------|
 * | `drone`   | harmonic bed, always audible        | every phase                       |
 * | `sub`     | pulse, gives the round a heartbeat  | `production` onwards              |
 * | `pluck`   | arpeggio, "you are deciding"        | `command`                         |
 * | `perc`    | drive, scaled by tension            | `contest` / `auction`             |
 * | `shimmer` | high air, "the clock is running out"| late rounds (round/total > 0.7)   |
 *
 * Harmonic tension: past 70% of the match (or at high `tension`) the chord voicings gain
 * a b9 and a suspended 4th, which sours the bed without changing the key. `finalScoring`
 * halves the tempo and strips everything but drone plus shimmer; `gameOver` locks the
 * progression onto the tonic **major** — the only consonant resolution in the game.
 *
 * This module imports types only, and knows nothing about the SFX engine: it is handed a
 * pre-faded output node and a reverb send by `audio.ts`.
 */

import type { Phase } from '../game/types';

/* ------------------------------------------------------------------ contract */

/** Everything the score needs from the host engine's mix bus. */
export interface MusicBus {
  readonly ctx: AudioContext;
  /** Post-music-fader summing node. */
  readonly out: AudioNode;
  /** Music's own pre-scaled reverb send. */
  readonly send: AudioNode;
  /** Shared white-noise table, reused for percussion and air. */
  readonly noise: AudioBuffer;
}

/* ----------------------------------------------------------------- constants */

const TEMPO = 78;
const STEPS_PER_BAR = 16;
const BARS = 4;
const TOTAL_STEPS = STEPS_PER_BAR * BARS;

/** Scheduler: wake every 25 ms, commit everything inside the next 200 ms. */
const TICK_MS = 25;
const LOOKAHEAD = 0.2;

/** D2 — the harmonic floor. Every layer is an octave transposition of this. */
const ROOT_HZ = 73.4162;

/** Smoothing constant for layer crossfades. Slow enough to feel like a mix move. */
const FADE_TAU = 0.9;

interface ChordDef {
  /** Semitones above the tonic. */
  readonly root: number;
  readonly minor: boolean;
}

/** i – VI – III – iv in D minor. Modal, unresolved, loops forever without fatigue. */
const PROGRESSION: readonly ChordDef[] = [
  { root: 0, minor: true }, // Dm
  { root: 8, minor: false }, // Bb
  { root: 3, minor: false }, // F
  { root: 5, minor: true }, // Gm
];

/** The one place the score is allowed to resolve. */
const TONIC_MAJOR: ChordDef = { root: 0, minor: false };

/* Step patterns, one bar of 16ths each. */
const SUB_PATTERN: readonly number[] = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0];
const PLUCK_PATTERN: readonly number[] = [1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 1];
const KICK_PATTERN: readonly number[] = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0];
const HAT_PATTERN: readonly number[] = [0, 0, 1, 0, 0, 1, 1, 0, 0, 0, 1, 0, 0, 1, 1, 1];
const SNAP_PATTERN: readonly number[] = [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1];

type LayerKey = 'drone' | 'sub' | 'pluck' | 'perc' | 'shimmer';

type Mix = Record<LayerKey, number>;

const SILENT: Readonly<Mix> = { drone: 0, sub: 0, pluck: 0, perc: 0, shimmer: 0 };

/** The persistent node graph, rebuilt on every `start()` after a `stop()` teardown. */
interface Graph {
  root: GainNode;
  layers: Record<LayerKey, GainNode>;
  /** Continuous drone oscillators plus their filter and slow motion LFO. */
  drones: OscillatorNode[];
  droneFilter: BiquadFilterNode;
  lfo: OscillatorNode;
  lfoDepth: GainNode;
  /** Dotted-eighth feedback delay, shared by pluck and shimmer. */
  delay: DelayNode;
  feedback: GainNode;
}

/* -------------------------------------------------------------------- engine */

export class MusicEngine {
  private readonly bus: MusicBus;

  private g: Graph | null = null;
  private running = false;
  private disposed = false;

  private timer: number | null = null;
  private teardown: number | null = null;

  private step = 0;
  private nextTime = 0;

  private phase: Phase = 'setup';
  private round = 1;
  private total = 8;
  private tension = 0;

  constructor(bus: MusicBus) {
    this.bus = bus;
  }

  /* ------------------------------------------------------------- lifecycle */

  /** Idempotent. Re-entrant across stop/start cycles, including mid-fade. */
  start(): void {
    if (this.disposed || this.running) return;
    if (this.teardown !== null) {
      // A previous stop() is still fading out — reclaim the graph instead of leaking it.
      clearTimeout(this.teardown);
      this.teardown = null;
    }
    try {
      if (!this.g) this.g = this.build();
      this.running = true;
      this.step = 0;
      this.nextTime = this.bus.ctx.currentTime + 0.12;
      this.applyMix(0.05);
      if (typeof window !== 'undefined') {
        this.timer = window.setInterval(() => this.tick(), TICK_MS);
      }
    } catch {
      this.running = false;
      this.g = null;
    }
  }

  /** Fade out, then drop the graph. Safe to call when already stopped. */
  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    const g = this.g;
    if (!g) return;
    const t = this.bus.ctx.currentTime;
    g.root.gain.cancelScheduledValues(t);
    g.root.gain.setTargetAtTime(0, t, 0.25);
    if (typeof window !== 'undefined') {
      this.teardown = window.setTimeout(() => {
        this.teardown = null;
        this.destroyGraph();
      }, 1400);
    }
  }

  dispose(): void {
    this.disposed = true;
    this.running = false;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.teardown !== null) {
      clearTimeout(this.teardown);
      this.teardown = null;
    }
    this.destroyGraph();
  }

  /* ------------------------------------------------------------- direction */

  setPhase(phase: Phase, round: number, totalRounds: number): void {
    const wasPhase = this.phase;
    this.phase = phase;
    this.round = Math.max(1, round);
    this.total = Math.max(1, totalRounds);
    // Restart the loop on the tonic so the resolution lands on the downbeat.
    if (phase !== wasPhase && (phase === 'gameOver' || phase === 'finalScoring')) {
      this.step = 0;
      if (this.running) this.nextTime = this.bus.ctx.currentTime + 0.08;
    }
    this.applyMix(FADE_TAU);
    this.retuneDrone(this.chord());
  }

  setTension(t: number): void {
    const v = Number.isFinite(t) ? Math.min(1, Math.max(0, t)) : 0;
    if (Math.abs(v - this.tension) < 0.01) return;
    this.tension = v;
    this.applyMix(0.4);
  }

  /* ------------------------------------------------------------- scheduler */

  /**
   * Lookahead scheduler. Everything inside the horizon is committed to the audio clock,
   * so a stalled main thread cannot make the score stutter.
   */
  private tick(): void {
    const g = this.g;
    if (!g || !this.running) return;
    const ctx = this.bus.ctx;
    if (ctx.state !== 'running') return;
    // Re-sync after a suspend/resume, rather than firing a burst of overdue notes.
    if (this.nextTime < ctx.currentTime) this.nextTime = ctx.currentTime + 0.05;
    const horizon = ctx.currentTime + LOOKAHEAD;
    let guard = 0;
    while (this.nextTime < horizon && guard++ < 32) {
      try {
        this.scheduleStep(g, this.step, this.nextTime);
      } catch {
        // A bad note is not worth stopping the music for.
      }
      this.nextTime += this.stepDuration();
      this.step = (this.step + 1) % TOTAL_STEPS;
      if (this.step % STEPS_PER_BAR === 0) this.applyMix(FADE_TAU);
    }
  }

  private stepDuration(): number {
    const sixteenth = 60 / TEMPO / 4;
    // Final scoring breathes: half tempo, wide and slow.
    return this.phase === 'finalScoring' ? sixteenth * 2 : sixteenth;
  }

  /** Emit every layer's contribution for one 16th-note step. */
  private scheduleStep(g: Graph, step: number, t: number): void {
    const inBar = step % STEPS_PER_BAR;
    const chord = this.chord();
    const tones = this.tones(chord);
    const sparse = this.phase === 'finalScoring';

    if (inBar === 0) this.retuneDrone(chord);

    // Sub pulse — the round's heartbeat, root of the current chord two octaves down.
    if (SUB_PATTERN[inBar] && !(sparse && inBar !== 0)) {
      this.sub(g, t, chord.root, sparse);
    }

    // Pluck arpeggio — wanders the chord tones so it never sounds like a loop.
    if (PLUCK_PATTERN[inBar] && !sparse) {
      const note = tones[(step * 3) % tones.length];
      this.pluck(g, t, note, 2 + ((step >> 2) % 2));
    }

    // Percussion — pattern is fixed; tension decides how much of it is allowed through.
    if (!sparse) this.percussion(g, t, inBar);

    // Shimmer — a slow high bell every half bar, plus air on the bar line.
    if (step % 8 === 0) {
      this.shimmer(g, t, tones[(step / 8) % tones.length], sparse);
    }
  }

  /* ---------------------------------------------------------------- layers */

  /** Current chord: the loop, unless the match is over and wants to resolve. */
  private chord(): ChordDef {
    if (this.phase === 'gameOver') return TONIC_MAJOR;
    const bar = Math.floor(this.step / STEPS_PER_BAR) % PROGRESSION.length;
    return PROGRESSION[bar];
  }

  /** True once the match is in its last 30%, or while a bid clock is screaming. */
  private tense(): boolean {
    return this.round / this.total > 0.7 || this.tension > 0.66;
  }

  /** Chord tones in semitones above the tonic, coloured by tension. */
  private tones(c: ChordDef): number[] {
    const base = c.minor ? [0, 3, 7, 10] : [0, 4, 7, 9];
    const t = base.map((n) => n + c.root);
    if (this.tense() && this.phase !== 'gameOver') {
      t.push(c.root + 13); // b9 — the sound of running out of time
      t.push(c.root + 5); // suspended 4th, left unresolved
    }
    return t;
  }

  /** Retune the sustained drone with a glide, so chord changes swell rather than cut. */
  private retuneDrone(c: ChordDef): void {
    const g = this.g;
    if (!g) return;
    const t = this.bus.ctx.currentTime;
    const colour = this.tense() ? c.root + 13 : c.root + (c.minor ? 3 : 4);
    const wide = this.phase === 'finalScoring';
    // root ×2 (octave apart), fifth, and the colour tone.
    const semis = [c.root, c.root + 12, c.root + 7, colour];
    for (let i = 0; i < g.drones.length; i++) {
      const osc = g.drones[i];
      const target = hz(semis[i % semis.length], i === 0 ? 0 : 1);
      osc.frequency.cancelScheduledValues(t);
      osc.frequency.setTargetAtTime(target, t, wide ? 1.6 : 0.7);
    }
    g.droneFilter.frequency.cancelScheduledValues(t);
    g.droneFilter.frequency.setTargetAtTime(
      wide ? 380 : 520 + this.tension * 700,
      t,
      1.2,
    );
  }

  /** Sine sub with a short pitch drop — felt more than heard. */
  private sub(g: Graph, t: number, root: number, sparse: boolean): void {
    const ctx = this.bus.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    const f = hz(root, -1);
    osc.frequency.setValueAtTime(f * 1.6, t);
    osc.frequency.exponentialRampToValueAtTime(f, t + 0.06);
    const amp = ctx.createGain();
    const dur = env(amp.gain, t, 0.9, 0.008, sparse ? 1.6 : 0.5);
    osc.connect(amp).connect(g.layers.sub);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  /** Short FM pluck through the shared delay — the "thinking" layer. */
  private pluck(g: Graph, t: number, semis: number, octave: number): void {
    const ctx = this.bus.ctx;
    const f = hz(semis, octave);
    const carrier = ctx.createOscillator();
    carrier.type = 'triangle';
    carrier.frequency.value = f;
    const mod = ctx.createOscillator();
    mod.type = 'sine';
    mod.frequency.value = f * 2.01;
    const idx = ctx.createGain();
    idx.gain.setValueAtTime(f * (0.6 + this.tension * 0.8), t);
    idx.gain.exponentialRampToValueAtTime(f * 0.01, t + 0.12);
    mod.connect(idx).connect(carrier.frequency);

    const amp = ctx.createGain();
    const dur = env(amp.gain, t, 0.5 + Math.random() * 0.18, 0.004, 0.28);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(2200 + this.tension * 2600, t);
    lp.Q.value = 1.1;

    carrier.connect(lp).connect(amp).connect(g.layers.pluck);
    amp.connect(g.delay);
    mod.start(t);
    carrier.start(t);
    mod.stop(t + dur + 0.05);
    carrier.stop(t + dur + 0.05);
  }

  /** Kick / hat / snap from the shared noise table plus a pitched sine for the kick. */
  private percussion(g: Graph, t: number, inBar: number): void {
    const ctx = this.bus.ctx;
    const T = this.tension;

    if (KICK_PATTERN[inBar]) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(110, t);
      osc.frequency.exponentialRampToValueAtTime(42, t + 0.09);
      const amp = ctx.createGain();
      const dur = env(amp.gain, t, 0.85, 0.003, 0.24);
      osc.connect(amp).connect(g.layers.perc);
      osc.start(t);
      osc.stop(t + dur + 0.03);
      this.noiseHit(g, t, { freq: 2000, q: 1, peak: 0.12, decay: 0.02 });
    }

    // Off-beat hats only survive once the tension justifies the extra density.
    if (HAT_PATTERN[inBar] && (inBar % 4 === 2 || T > 0.4)) {
      this.noiseHit(g, t, {
        freq: 7200 + Math.random() * 900,
        q: 2.4,
        peak: 0.12 + T * 0.12,
        decay: inBar % 4 === 2 ? 0.045 : 0.025,
      });
    }

    if (SNAP_PATTERN[inBar] && T > 0.25) {
      this.noiseHit(g, t, { freq: 1900, q: 1.4, peak: 0.16 + T * 0.18, decay: 0.09 });
    }
  }

  /** Filtered noise transient, the percussion workhorse. */
  private noiseHit(
    g: Graph,
    t: number,
    c: { freq: number; q: number; peak: number; decay: number },
  ): void {
    const ctx = this.bus.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.bus.noise;
    src.loop = true;
    const bq = ctx.createBiquadFilter();
    bq.type = 'bandpass';
    bq.frequency.value = c.freq;
    bq.Q.value = c.q;
    const amp = ctx.createGain();
    const dur = env(amp.gain, t, c.peak, 0.001, c.decay);
    src.connect(bq).connect(amp).connect(g.layers.perc);
    src.start(t, Math.random() * 1.2, dur + 0.02);
  }

  /** High, slow bell with a long reverb send — late-game air. */
  private shimmer(g: Graph, t: number, semis: number, sparse: boolean): void {
    const ctx = this.bus.ctx;
    const f = hz(semis, sparse ? 3 : 4);
    const carrier = ctx.createOscillator();
    carrier.type = 'sine';
    carrier.frequency.value = f;
    const mod = ctx.createOscillator();
    mod.type = 'sine';
    mod.frequency.value = f * 3.01;
    const idx = ctx.createGain();
    idx.gain.setValueAtTime(f * 0.35, t);
    idx.gain.exponentialRampToValueAtTime(f * 0.01, t + 0.5);
    mod.connect(idx).connect(carrier.frequency);

    const amp = ctx.createGain();
    const dur = env(amp.gain, t, 0.28, sparse ? 0.5 : 0.12, sparse ? 3.0 : 1.6);
    carrier.connect(amp).connect(g.layers.shimmer);
    amp.connect(g.delay);
    mod.start(t);
    carrier.start(t);
    mod.stop(t + dur + 0.05);
    carrier.stop(t + dur + 0.05);
  }

  /* ------------------------------------------------------------------- mix */

  /** Target level per layer for the current phase, round and tension. */
  private mix(): Mix {
    const late = this.round / this.total > 0.7;
    const T = this.tension;
    const air = late ? 0.3 : 0.07;
    switch (this.phase) {
      case 'setup':
        return { ...SILENT, drone: 0.35, shimmer: 0.1 };
      case 'drift':
        return { ...SILENT, drone: 0.55, shimmer: air, perc: 0.1 * T };
      case 'production':
        return { ...SILENT, drone: 0.5, sub: 0.55, shimmer: air, perc: 0.14 * T };
      case 'command':
        return {
          drone: 0.45,
          sub: 0.4,
          pluck: 0.6,
          perc: 0.18 + 0.24 * T,
          shimmer: air,
        };
      case 'contest':
        return {
          drone: 0.5,
          sub: 0.62,
          pluck: 0.14,
          perc: 0.45 + 0.5 * T,
          shimmer: late ? 0.2 : 0,
        };
      case 'auction':
        return {
          drone: 0.5,
          sub: 0.5,
          pluck: 0.26,
          perc: 0.34 + 0.45 * T,
          shimmer: late ? 0.24 : 0.05,
        };
      case 'upkeep':
        return { ...SILENT, drone: 0.45, sub: 0.3, shimmer: air };
      case 'finalScoring':
        return { ...SILENT, drone: 0.42, sub: 0.16, shimmer: 0.36 };
      case 'gameOver':
        return { ...SILENT, drone: 0.45, sub: 0.2, pluck: 0.34, shimmer: 0.5 };
      default:
        return { ...SILENT, drone: 0.35 };
    }
  }

  /** Crossfade every layer towards its target. Called on change and once per bar. */
  private applyMix(tau: number): void {
    const g = this.g;
    if (!g) return;
    const t = this.bus.ctx.currentTime;
    const target = this.mix();
    g.root.gain.cancelScheduledValues(t);
    g.root.gain.setTargetAtTime(this.running ? 1 : 0, t, Math.min(tau, 0.3));
    for (const key of Object.keys(target) as LayerKey[]) {
      const p = g.layers[key].gain;
      p.cancelScheduledValues(t);
      p.setTargetAtTime(target[key], t, Math.max(0.05, tau));
    }
  }

  /* ----------------------------------------------------------------- graph */

  private build(): Graph {
    const ctx = this.bus.ctx;

    const root = ctx.createGain();
    root.gain.value = 0;
    root.connect(this.bus.out);
    // The score is always partly wet: reverb is what makes four oscillators feel big.
    const wet = ctx.createGain();
    wet.gain.value = 0.55;
    root.connect(wet);
    wet.connect(this.bus.send);

    const layers: Record<LayerKey, GainNode> = {
      drone: ctx.createGain(),
      sub: ctx.createGain(),
      pluck: ctx.createGain(),
      perc: ctx.createGain(),
      shimmer: ctx.createGain(),
    };
    for (const key of Object.keys(layers) as LayerKey[]) {
      layers[key].gain.value = 0;
      layers[key].connect(root);
    }

    // Dotted-eighth feedback delay shared by the melodic layers.
    const delay = ctx.createDelay(2);
    delay.delayTime.value = (60 / TEMPO / 4) * 3;
    const feedback = ctx.createGain();
    feedback.gain.value = 0.34;
    const damp = ctx.createBiquadFilter();
    damp.type = 'lowpass';
    damp.frequency.value = 2400;
    delay.connect(damp).connect(feedback).connect(delay);
    const echoLevel = ctx.createGain();
    echoLevel.gain.value = 0.4;
    delay.connect(echoLevel).connect(root);

    // Drone: four detuned oscillators, spread hard across the field in pairs, through a
    // shared lowpass that a very slow LFO keeps moving so the bed never sits still.
    const droneFilter = ctx.createBiquadFilter();
    droneFilter.type = 'lowpass';
    droneFilter.frequency.value = 520;
    droneFilter.Q.value = 1.4;
    droneFilter.connect(layers.drone);

    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.06;
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = 180;
    lfo.connect(lfoDepth).connect(droneFilter.frequency);

    const drones: OscillatorNode[] = [];
    const shapes: OscillatorType[] = ['sawtooth', 'triangle', 'sawtooth', 'triangle'];
    for (let i = 0; i < 4; i++) {
      const osc = ctx.createOscillator();
      osc.type = shapes[i];
      osc.frequency.value = hz(i === 2 ? 7 : 0, i === 0 ? 0 : 1);
      osc.detune.value = (i - 1.5) * 7; // a few cents apart: slow, organic beating
      const pan = ctx.createStereoPanner();
      pan.pan.value = i % 2 === 0 ? -0.55 : 0.55;
      const lvl = ctx.createGain();
      lvl.gain.value = i === 3 ? 0.16 : 0.3; // the colour tone stays underneath
      osc.connect(lvl).connect(pan).connect(droneFilter);
      drones.push(osc);
    }

    const t0 = ctx.currentTime;
    lfo.start(t0);
    for (const osc of drones) osc.start(t0);

    return { root, layers, drones, droneFilter, lfo, lfoDepth, delay, feedback };
  }

  private destroyGraph(): void {
    const g = this.g;
    this.g = null;
    if (!g) return;
    const t = this.bus.ctx.currentTime;
    try {
      for (const osc of g.drones) osc.stop(t);
      g.lfo.stop(t);
    } catch {
      /* already stopped */
    }
    try {
      g.feedback.disconnect();
      g.delay.disconnect();
      g.root.disconnect();
    } catch {
      /* already detached */
    }
  }
}

/* ------------------------------------------------------------------ helpers */

/** Equal-tempered pitch: `semis` above D2, transposed by `octave` octaves. */
function hz(semis: number, octave = 0): number {
  return ROOT_HZ * Math.pow(2, semis / 12 + octave);
}

/** Percussive AD envelope; returns its total length in seconds. */
function env(p: AudioParam, t: number, peak: number, attack: number, decay: number): number {
  const a = Math.max(0.0008, attack);
  const d = Math.max(0.01, decay);
  const top = Math.max(0.0004, peak);
  p.setValueAtTime(0.0001, t);
  p.linearRampToValueAtTime(top, t + a);
  p.exponentialRampToValueAtTime(0.0001, t + a + d);
  p.setValueAtTime(0, t + a + d + 0.002);
  return a + d + 0.004;
}
